import { type Theme, getTheme } from '@/stores/preferences.store'
import { createEffect, onCleanup, onMount } from 'solid-js'

export function useTheme() {
  const isDarkModeRoute = (path: string) => {
    return path.startsWith('/app/') || path.startsWith('/admin/')
  }

  const getCurrentPath = () => window.location.pathname

  const applyTheme = (path: string, theme: Theme) => {
    const root = document.documentElement
    if (root.hasAttribute('data-theme-hold')) {
      return
    }

    if (!isDarkModeRoute(path)) {
      root.removeAttribute('data-theme')
      return
    }

    if (theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      root.setAttribute('data-theme', prefersDark ? 'dark' : 'light')
    } else {
      root.setAttribute('data-theme', theme)
    }
  }

  let mediaQuery: MediaQueryList | null = null
  let systemPrefHandler: (() => void) | null = null

  onMount(() => {
    applyTheme(getCurrentPath(), getTheme())

    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    systemPrefHandler = () => applyTheme(getCurrentPath(), getTheme())
    mediaQuery.addEventListener('change', systemPrefHandler)

    const handleRouteChange = () => {
      applyTheme(getCurrentPath(), getTheme())
    }
    window.addEventListener('popstate', handleRouteChange)

    const observer = new MutationObserver(() => {
      const currentPath = getCurrentPath()
      applyTheme(currentPath, getTheme())
    })

    observer.observe(document.body, { childList: true, subtree: true })

    onCleanup(() => {
      window.removeEventListener('popstate', handleRouteChange)
      observer.disconnect()
    })
  })

  onCleanup(() => {
    if (mediaQuery && systemPrefHandler) {
      mediaQuery.removeEventListener('change', systemPrefHandler)
    }
  })

  createEffect(() => {
    const theme = getTheme()
    applyTheme(getCurrentPath(), theme)
  })
}
